import {
    collection,
    CollectionReference,
    DocumentData,
    serverTimestamp,
    Timestamp,
    getCountFromServer,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    query,
    where,
    limit,
    orderBy,
    increment,
    onSnapshot,
    setDoc
} from "firebase/firestore";

import { fsB, fs } from "../firebase.ts";
import { Reader, Response } from "../models/Reader.ts";

// can be mobile businesses too?
export class Readers {
    private collection: CollectionReference<DocumentData>;
    private unsubscribe?: () => void;
    public isRealTime = false;

    constructor() {
        this.collection = collection(fs, "readers");
    }

    stop() {
        this.isRealTime = false;
        if (this.unsubscribe) this.unsubscribe();
    }

    async getUser(uid: string) {
        if (uid) {
            const docRef = doc(fsB, "users", uid);
            const docSnap = await getDoc(docRef);
            return docSnap.exists() ? docSnap.data() : {}; // should always exist
        } else return {};
    }

    async count(): Promise<number> {
        const snapshot = await getCountFromServer(this.collection);
        return snapshot.data().count;
    }

    // device id
    async get(id: string): Promise<Reader | null> {
        const docRef = doc(fs, "readers", id);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? (docSnap.data() as Reader) : null;
    }

    // deprecated
    async get2(did: string): Promise<Reader | null> {
        const q = query(this.collection, where("did", "==", did), limit(1));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const doc = snapshot.docs[0];
            return { id: doc.id, ...doc.data() } as Reader;
        }
        return null;
    }

    // deprecated
    async get3(uid: string): Promise<Reader | null> {
        const q = query(this.collection, where("uid", "==", uid), limit(1));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const doc = snapshot.docs[0];
            return { id: doc.id, ...doc.data() } as Reader;
        }
        return null;
    }

    // logged in = public; logged out = anonymous
    async update(visitor: Reader): Promise<Response> {
        try {
            const docRef = doc(fs, "readers", visitor.id!);
            const reader = await this.get(visitor.id!);

            reader
                ? await updateDoc(docRef, {
                      lastActive: serverTimestamp(),
                      pages: increment(1),
                      uid: visitor.uid, // can change if log in as another user on the same device
                      user: visitor.user,
                      address: visitor.address,
                      latitude: visitor.latitude,
                      longitude: visitor.longitude
                  })
                : await setDoc(docRef, {
                      date: serverTimestamp(),
                      lastActive: serverTimestamp(),
                      pages: increment(1),
                      ...visitor // causes duplicate id with doc id
                  });

            const docSnap = await getDoc(docRef);
            const data = docSnap.data() as Reader;
            visitor.date = data.date;
            visitor.lastActive = data.lastActive;
            visitor.pages = data.pages;

            return {
                status: "success",
                reader: {
                    id: docRef.id,
                    ...visitor
                }
            };
        } catch (error: any) {
            return { status: "error", message: error.message };
        }
    }

    // deprecated
    async update2(visitor: Reader): Promise<Response> {
        try {
            const reader = visitor.uid
                ? await this.get3(visitor.uid)
                : await this.get2(visitor.id!); //did

            if (reader && reader.id) {
                const docRef = doc(fs, "readers", reader.id);
                await updateDoc(docRef, {
                    lastActive: serverTimestamp(),
                    pages: increment(1),
                    user: visitor.user,
                    address: visitor.address,
                    latitude: visitor.latitude,
                    longitude: visitor.longitude
                });

                //const docSnap = await getDoc(docRef);
                return {
                    status: "success",
                    reader // missing updated stuff but not needed
                };
            } else {
                const docRef = await addDoc(this.collection, {
                    date: serverTimestamp(),
                    lastActive: serverTimestamp(),
                    pages: increment(1),
                    ...visitor
                });

                //const docSnap = await getDoc(docRef);
                return {
                    status: "success",
                    reader: {
                        id: docRef.id,
                        ...visitor // missing updated stuff but not needed
                    }
                };
            }
        } catch (error: any) {
            return { status: "error", message: error.message };
        }
    }

    // realtime
    async now(callback: (readers: Reader[], changes: Reader[]) => void) {
        // one year for now: later 1 month, 1 week, 1 day
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

        // index not needed
        const q = query(
            this.collection,
            where("lastActive", ">", Timestamp.fromDate(oneYearAgo)),
            orderBy("lastActive", "desc"), // for ranking
            limit(100)
        );

        this.stop();
        this.isRealTime = true;
        this.unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                // if snapshot.empty
                const readers = snapshot.docs.map(
                    (doc, index) =>
                        ({
                            id: doc.id,
                            rank: index + 1,
                            ...doc.data()
                        }) as Reader
                );

                // Optional: handle per-change updates
                const changes: Reader[] = [];
                snapshot.docChanges().map((change) => {
                    if (change.type === "added")
                        //console.log("Added:", change.doc.data());
                        changes.push({
                            id: change.doc.id,
                            ...change.doc.data()
                        } as Reader);
                    // if (change.type === "modified")
                    //     console.log("Modified:", change.doc.data());
                    // if (change.type === "removed")
                    //     console.log("Removed:", change.doc.data());
                });

                // readers.reverse()
                // oldest → newest
                readers.sort(
                    (a, b) =>
                        a.lastActive!.nanoseconds - b.lastActive!.nanoseconds
                );

                if (this.isRealTime) callback(readers, changes);
            },
            (error) => {
                console.error("Error listening to document: ", error.message);
            }
        );
    }

    async leaderboard(): Promise<Reader[]> {
        this.stop();

        // index needed bc compound
        const q = query(
            this.collection,
            orderBy("pages", "desc"),
            orderBy("lastActive", "desc"), // to break tie
            limit(50)
        );

        const querySnapshot = await getDocs(q);
        const readers = querySnapshot.docs.map(
            (doc, index) =>
                ({
                    id: doc.id,
                    rank: index + 1,
                    ...doc.data()
                }) as Reader
        );

        // oldest → newest
        readers.sort(
            (a, b) => a.lastActive!.nanoseconds - b.lastActive!.nanoseconds
        );

        return readers; //.reverse();

        // let readers: Reader[] = [];
        // if (!querySnapshot.empty) {
        //     readers = querySnapshot.docs.map(
        //         (doc, index) =>
        //             ({
        //                 id: doc.id,
        //                 rank: index + 1,
        //                 ...doc.data()
        //             } as Reader)
        //     );
        // }
    }

    // not used for now: fake data now
    async history(): Promise<Reader[]> {
        this.stop();

        // index not needed
        const q = query(
            this.collection,
            orderBy("date", "asc"), // for ranking
            limit(1000)
        );

        const querySnapshot = await getDocs(q);
        const readers = querySnapshot.docs.map(
            (doc, index) =>
                ({
                    id: doc.id,
                    rank: index + 1,
                    ...doc.data()
                }) as Reader
        );

        // oldest → newest
        readers.sort(
            (a, b) => a.lastActive!.nanoseconds - b.lastActive!.nanoseconds
        );

        return readers; //.reverse();

        // let readers: Reader[] = [];
        // if (!querySnapshot.empty) {
        //     readers = querySnapshot.docs.map(
        //         (doc) =>
        //             ({
        //                 id: doc.id,
        //                 ...doc.data()
        //             } as Reader)
        //     );
        // }
    }
}
